import { v4 as uuidv4 } from 'uuid'
import ErrorModal, { ErrorButton } from '@/components/ErrorModal'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  canPayWithApplePayAtom,
  canPayWithGooglePayAtom,
  orderingStatusAtom,
  payWithApplePayAtom,
  payWithGooglePayAtom,
  paymentMethodsAtom,
  sessionExpiredAtom,
  venueAtom,
  verifyComponentAtom,
  tabUuidAtom,
  verifyStepAtom,
  userInfoAtom,
} from '@/state/global'
import {
  PaymentDataAtom,
  switchedPaymentMethodAtom,
  cardAuthRequestTypeAtom,
  CardAuthRequestTypes,
  paymentsMethodModalAtom,
  retryCardAuthAtom,
} from '@/state/payment'
import {
  closeTabModalIsOpenAtom,
  hasCardIncAuthErrorAtom,
  hasPlacedAnOrderOnTabAtom,
  isNextRoundOpenAtom,
  menuModalAtom,
  orderAtom,
  orderUuidAtom,
  runningTabIsEmptyAtom,
  viewOrderAtom,
} from '@/state/order'
import { newRelicLog } from '@/utils/newrelic'
import { PaymentMethodInputTypes } from '@/utils/constants'
import { NEXT_ROUND_ORDER_KEY, POST_ORDER_AD_SEEN_KEY } from '@/constants'
import { handleAuthError } from '@/utils/authErrorHandler'

type MutationFunction = (...args: any[]) => Promise<any>

type ErrorMessage = {
  title: string
  description: string
  entities: string[]
  primaryButton: ErrorButton
  secondaryButtons: ErrorButton[]
}

function useMutationWithRedirect<T>({
  mutationFn,
  onSuccess,
  onMutate,
  onError,
  error,
}: {
  mutationFn: MutationFunction
  redirectTo?: string
  onMutate?: () => void
  onError?: (data: unknown) => void
  onSuccess?: (data: any) => void
  error?: React.JSX.Element
}) {
  const router = useRouter()

  const { venue_uuid, ordering } = router.query
  const safeVenueUuid = Array.isArray(venue_uuid) ? venue_uuid[0] ?? '' : venue_uuid ?? ''
  const pathname = router.pathname
  const cardAuthRequestType = useAtomValue(cardAuthRequestTypeAtom)
  const openVerifyComponent = useSetAtom(verifyComponentAtom)
  const setSessionExpired = useSetAtom(sessionExpiredAtom)
  const [runningOrder, setRunningOrder] = useAtom(orderAtom)
  const setIsOrderOpen = useSetAtom(viewOrderAtom)
  const venueDetails = useAtomValue(venueAtom)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useAtom(PaymentDataAtom)
  const [paymentMethods, setPaymentMethods] = useAtom(paymentMethodsAtom)
  const [hasPlacedAnOrderOnTab, setHasPlacedAnOrderOnTab] = useAtom(hasPlacedAnOrderOnTabAtom)
  const isUserAbleToOrder = useAtomValue(orderingStatusAtom)
  const setDidSwitchPaymentMethod = useSetAtom(switchedPaymentMethodAtom)
  const canPayWithApplePay = useAtomValue(canPayWithApplePayAtom)
  const canPayWithGooglePay = useAtomValue(canPayWithGooglePayAtom)
  const setPayWithApplePay = useSetAtom(payWithApplePayAtom)
  const setPayWithGooglePay = useSetAtom(payWithGooglePayAtom)
  const setPaymentsMethodModalIsOpen = useSetAtom(paymentsMethodModalAtom)
  const setShouldRetryCard = useSetAtom(retryCardAuthAtom)
  const [runningTabIsEmpty, setRunningTabIsEmpty] = useAtom(runningTabIsEmptyAtom)
  const [savedTabUuid, setSavedTabUuid] = useAtom(tabUuidAtom)
  const setHasCardIncAuthError = useSetAtom(hasCardIncAuthErrorAtom)
  const setVerifyStep = useSetAtom(verifyStepAtom)
  const user = useAtomValue(userInfoAtom)

  // cancel order state
  const deleteOrder = useSetAtom(orderAtom)
  const setOrderUUID = useSetAtom(orderUuidAtom)
  const closeOrderModal = useSetAtom(viewOrderAtom)
  const closeMenu = useSetAtom(menuModalAtom)
  const setCloseTabModalIsOpen = useSetAtom(closeTabModalIsOpenAtom)
  const setIsNextRoundOpen = useSetAtom(isNextRoundOpenAtom)

  const [errorMessage, setErrorMessage] = useState<ErrorMessage>({
    title: 'Something went wrong',
    description: 'Please try Again',
    entities: [],
    primaryButton: { name: 'Try Again', action: () => {} },
    secondaryButtons: [],
  })
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const handleTabIsClosed = () => {
    // Filter out payment methods that are not manually keyed.
    const manualKeyedPaymentMethods = paymentMethods.filter((pm) => {
      return ![
        PaymentMethodInputTypes.FORTIS_APPLE_PAY_WEB,
        PaymentMethodInputTypes.FORTIS_GOOGLE_PAY_WEB,
        PaymentMethodInputTypes.MAGENSA_UNIGATE_GOOGLE_PAY_WEB,
        PaymentMethodInputTypes.MAGENSA_UNIGATE_APPLE_PAY_WEB,
      ].includes(pm?.input_mode)
    })
    setPaymentMethods(manualKeyedPaymentMethods)

    // Check if the selected payment method is one of the automated types.
    if (
      selectedPaymentMethod?.input_mode &&
      [
        PaymentMethodInputTypes.FORTIS_APPLE_PAY_WEB,
        PaymentMethodInputTypes.FORTIS_GOOGLE_PAY_WEB,
        PaymentMethodInputTypes.MAGENSA_UNIGATE_GOOGLE_PAY_WEB,
        PaymentMethodInputTypes.MAGENSA_UNIGATE_APPLE_PAY_WEB,
      ].includes(selectedPaymentMethod.input_mode)
    ) {
      setSelectedPaymentMethod(null) // Clear the selected payment method if it's an automated type.
    }

    setHasPlacedAnOrderOnTab(false)
    setDidSwitchPaymentMethod(false)

    setRunningOrder([])
    localStorage.removeItem('order')
    setIsOrderOpen(false)
    closeMenu(false)
    localStorage.removeItem(POST_ORDER_AD_SEEN_KEY)

    // Navigate to a new URL with venue_uuid query parameter.
    router.push(`/?venue_uuid=${safeVenueUuid}${ordering === 'false' ? '&ordering=false' : ''}`)
  }

  const mutation = useMutation(mutationFn, {
    onSuccess: (data) => {
      onSuccess?.(data)
    },
    onError: async (error: any) => {
      //handles case when user refreshes when a tab would error, but doesn't get backend error yet
      const urlHasTab = pathname.includes('/tab') || pathname.includes('tab_uuid')
      if (!error && !urlHasTab) {
        setHasPlacedAnOrderOnTab(false)
        setSavedTabUuid('')
      }
      if (
        await handleAuthError(error, {
          onError,
          setSessionExpired,
          openVerifyComponent,
          setVerifyStep,
          user,
        })
      ) {
        return
      }

      let shouldOpenErrorModal = true
      const errorModalData = getMessage(error, pathname)
      const errorDefault = error?.default?.length > 0 ? error.default[0] : 'none'

      if (errorDefault === 'tab_already_open') {
        shouldOpenErrorModal = false
        const errorTabUuid = (error?.errors?.tab_uuid[0] as string) ?? ''
        setSavedTabUuid(errorTabUuid)
        if (errorTabUuid) {
          router.push(
            `/tab/?venue_uuid=${safeVenueUuid}&tab_uuid=${errorTabUuid}${ordering === 'false' ? '&ordering=false' : ''}`
          )
        }
      }

      if (errorModalData.title === 'Card Declined') {
        if (cardAuthRequestType === CardAuthRequestTypes.CHECKIN) {
          setHasPlacedAnOrderOnTab(false)
          setSavedTabUuid('')
          errorModalData.primaryButton.name = 'Close'
          setSelectedPaymentMethod(null)
          if (canPayWithApplePay) {
            setPayWithApplePay(true)
          } else if (canPayWithGooglePay) {
            setPayWithGooglePay(true)
          }
          errorModalData.secondaryButtons = []
          errorModalData.primaryButton.action = () => {}
        } else if (cardAuthRequestType === CardAuthRequestTypes.SUBMIT_ORDER) {
          setHasCardIncAuthError(true)
          const retryCardButton = {
            name: 'Retry Card',
            action: () => {
              setOrderUUID(uuidv4().toUpperCase())
              setShouldRetryCard(true)
            },
          } //action should submit order or checkin again

          const cancelOrderButton = runningTabIsEmpty
            ? {
                name: 'Cancel Order',
                action: () => {
                  deleteOrder([])
                  setOrderUUID(uuidv4().toUpperCase())
                  localStorage.removeItem('order')
                  closeOrderModal(false)
                  closeMenu(false)
                  router.push(
                    `/tab/?venue_uuid=${safeVenueUuid}&tab_uuid=${savedTabUuid}${ordering === 'false' ? '&ordering=false' : ''}`
                  )
                },
              }
            : {
                name: 'Cancel Order and Close Tab',
                action: () => {
                  deleteOrder([])
                  setOrderUUID(uuidv4().toUpperCase())
                  localStorage.removeItem('order')
                  closeOrderModal(false)
                  closeMenu(false)
                  setCloseTabModalIsOpen(true)
                  setIsNextRoundOpen(false)
                },
              }

          errorModalData.secondaryButtons = [retryCardButton, cancelOrderButton]
          errorModalData.description = 'Sorry, your card declined, and your order was not sent.'
          errorModalData.primaryButton.action = () => {
            setOrderUUID(uuidv4().toUpperCase())
            setPaymentsMethodModalIsOpen(true)
          }
        } else if (cardAuthRequestType === CardAuthRequestTypes.PAYMENT) {
          const retryCardButton = {
            name: 'Retry Card',
            action: () => {
              setShouldRetryCard(true)
            },
          }
          errorModalData.secondaryButtons = [retryCardButton]
          if (
            selectedPaymentMethod?.input_mode === PaymentMethodInputTypes.FORTIS_GOOGLE_PAY_WEB ||
            selectedPaymentMethod?.input_mode === PaymentMethodInputTypes.FORTIS_APPLE_PAY_WEB ||
            selectedPaymentMethod?.input_mode === PaymentMethodInputTypes.MAGENSA_UNIGATE_GOOGLE_PAY_WEB ||
            selectedPaymentMethod?.input_mode === PaymentMethodInputTypes.MAGENSA_UNIGATE_APPLE_PAY_WEB
          ) {
            setSelectedPaymentMethod(null)
            if (canPayWithApplePay) {
              setPayWithApplePay(true)
            } else if (canPayWithGooglePay) {
              setPayWithGooglePay(true)
            }
          }
        }
      }
      if (errorDefault === 'offers_not_redeemed') {
        const offerIdsFromError = error?.errors?.offers_not_redeemed?.map(
          (offerError: { id: number; reason: string }) => offerError.id
        )
        const venueOffersFromError = venueDetails.offers.filter((venueOffer) => offerIdsFromError.includes(venueOffer.offer_id))

        const fixedItems = runningOrder.map((item) => {
          const fixedOffers = item.offers.filter((offer) => !offerIdsFromError.includes(offer.offer_id))
          item.offers = fixedOffers
          return item
        })

        setRunningOrder(fixedItems)
        localStorage.setItem('order', JSON.stringify(fixedItems))

        const offerNamesInError = venueOffersFromError.map((offer) => {
          return offer.union_message
        })

        const offerNamesFormatted = offerNamesInError.join(', ')
        errorModalData.description =
          offerNamesFormatted + ' has already been used. Please check back another time for more Union rewards.'
      }
      if (errorModalData.title === 'Tab Is Closed') {
        errorModalData.primaryButton.action = handleTabIsClosed
      }

      setOpenErrorModal(shouldOpenErrorModal)
      setErrorMessage(errorModalData)
      onError?.(error)
    },
    onMutate: () => {
      onMutate?.()
    },
  })

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false)
  }

  return { mutation, error: <ErrorModal {...errorMessage} isOpen={openErrorModal} close={handleCloseErrorModal} /> }
}

export default useMutationWithRedirect

const getMessage = (error: any, pathname: string): ErrorMessage => {
  let errorMessage: ErrorMessage = {
    title: 'Something went wrong',
    description: 'Please try again.',
    entities: [],
    primaryButton: { name: 'Try Again', action: () => {} },
    secondaryButtons: [],
  }
  if (!error) {
    return errorMessage
  }

  const errorDefault = error?.default?.length > 0 ? error.default[0] : 'none'

  const message = error?.message

  switch (errorDefault) {
    case 'tab_already_open':
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: errorDefault,
          errorMessage: `tab_uuid is ${error?.errors?.tab_uuid[0]}`,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      break

    // this error have multiple sub errors
    case 'pos_connection_timeout':
      // lets make it easier by spliting them
      if (pathname.includes('tab')) {
        // if we are in a tab lets check based on the message
        switch (message) {
          case 'Could not submit mobile order':
            errorMessage.title = 'Unable to submit order'
            errorMessage.description = 'Please order with your server or try again.'
            newRelicLog(
              {
                name: 'webclip-error',
                errorDomain: errorDefault,
                errorMessage: message,
                errorDescription: JSON.stringify(error?.errors),
              },
              'pageAction'
            )
            break

          default:
            errorMessage.title = 'Unable to Close Tab'
            errorMessage.description = 'Unable to close your tab. Please try again in a moment'
            newRelicLog(
              {
                name: 'webclip-error',
                errorDomain: errorDefault,
                errorMessage: message,
                errorDescription: JSON.stringify(error?.errors),
              },
              'pageAction'
            )
            break
        }
        break
      } else {
        errorMessage.title = 'Unable to Open Tab'
        errorMessage.description = 'Please ask your server to open your tab or try again.'
        newRelicLog(
          {
            name: 'webclip-error',
            errorDomain: errorDefault,
            errorMessage: message,
            errorDescription: JSON.stringify(error?.errors),
          },
          'pageAction'
        )
      }
      break
    case 'card_expired':
      errorMessage.description = 'Please update your payment method.'
      errorMessage.title = 'Card Expired'
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: errorDefault,
          errorMessage: message,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      break
    case 'card_invalid':
    case 'card_out_of_balance':
    case 'incremental_auth_card_declined':
    case 'incremental_auth_card_out_of_balance':
    case 'card_declined':
      // we'll set the basic version here. in the component above we'll chekc app state and modify as needed
      errorMessage.description = 'Please use another payment method.'
      errorMessage.title = 'Card Declined'

      errorMessage.primaryButton.name = 'Change Payment Method'
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: errorDefault,
          errorMessage: message,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      break
    case 'items_out_of_stock':
      errorMessage.description = "Please modify the order to remove the following items. We're sorry for the inconvenience!"
      errorMessage.title = 'One or more items unavailable'
      errorMessage.entities = error?.errors?.items_out_of_stock.map((item: { name: string }) => item.name)
      errorMessage.primaryButton.name = 'Modify Order'
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: errorDefault,
          errorMessage: message,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      break
    case 'offers_not_redeemed':
      errorMessage.description = 'This offer has already been used. Please check back another time for more Union rewards.'
      errorMessage.title = "I'm sorry"
      errorMessage.entities = []
      errorMessage.primaryButton.name = 'Close'
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: errorDefault,
          errorMessage: message,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      break
    case 'rate_limited':
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: errorDefault,
          errorMessage: message,
          errorDescription: JSON.stringify(error?.errors),
        },
        'pageAction'
      )
      break
    default:
      if (error.errors?.generic_error) {
        if (error.errors?.generic_error.length > 0 && error.errors?.generic_error[0] === 'Tab is closed') {
          errorMessage.title = 'Tab Is Closed'
          errorMessage.description = 'This tab has already been closed.'
          errorMessage.primaryButton.name = 'Close'
        }
        break
      }
      if (error.errors?.code) {
        errorMessage.description = 'Verification code is not valid.'
        errorMessage.title = 'Code Invalid'
        newRelicLog(
          {
            name: 'webclip-error',
            errorDomain: errorDefault,
            errorMessage: message,
            errorDescription: JSON.stringify(error?.errors),
          },
          'pageAction'
        )
        break
      }

      if (error?.title === 'Invalid Data') {
        const description = error.description
        errorMessage.title = 'Invalid Information'
        if (description?.phone) {
          errorMessage.description = 'Please enter a valid phone number.'
        } else if (description?.email) {
          errorMessage.description = 'Please enter a valid email.'
        }
        newRelicLog(
          {
            name: 'webclip-error',
            errorDomain: errorDefault,
            errorMessage: message,
            errorDescription: JSON.stringify(error?.errors),
          },
          'pageAction'
        )
        break
      } else if (error?.message === 'Please enter valid phone number') {
        errorMessage.title = 'Invalid Information'
        errorMessage.description = 'Please enter a valid phone number.'
        newRelicLog(
          {
            name: 'webclip-error',
            errorDomain: errorDefault,
            errorMessage: message,
            errorDescription: JSON.stringify(error?.errors),
          },
          'pageAction'
        )
        break
      }
      if (pathname.includes('tab')) {
        errorMessage.description = 'It looks like some changes were made to your tab. Refreshing now…'
        newRelicLog(
          {
            name: 'webclip-error',
            errorDomain: errorDefault,
            errorMessage: message,
            errorDescription: JSON.stringify(error?.errors),
          },
          'pageAction'
        )
      }
      errorMessage.title = 'Something Went Wrong'
      break
  }
  return errorMessage
}
